// Detail-page model helpers. A detail page references devices that live on the
// standard pages (by id) and renders them with ALL their ports. Connections are
// derived from the standard pages' edges: edges between two members become
// internal port-to-port links; edges to non-members become labelled stubs.
import { deviceLabel } from './deviceIcons';

// Shared box layout — used identically by the React canvas node and the SVG/PDF
// renderer so positions line up exactly. Sizes are multiples of the 16px snap
// grid so port connection points land on grid lines (enables straight cables).
export const DLAYOUT = {
  BOX_W: 224,
  HEADER_H: 48,
  PORT_H: 32,
  PAD_B: 16,
  MIN_PORT_H: 16, // compact floor for very port-dense devices (e.g. 48-port)
  MAX_BODY_H: 980, // tallest a device box may get before rows are compressed
};

// Row height for a device's port rows. Stays at the comfortable default until
// the full box would exceed the printable sheet body, then compresses rows
// down to MIN_PORT_H so even a 48-port switch fits on one A3 page.
export function detailRowHeight(portCount) {
  const n = Math.max(portCount, 1);
  const full = DLAYOUT.HEADER_H + n * DLAYOUT.PORT_H + DLAYOUT.PAD_B;
  if (full <= DLAYOUT.MAX_BODY_H) return DLAYOUT.PORT_H;
  const avail = DLAYOUT.MAX_BODY_H - DLAYOUT.HEADER_H - DLAYOUT.PAD_B;
  return Math.max(DLAYOUT.MIN_PORT_H, Math.floor(avail / n));
}

// Port-name / badge font size scaled to the (possibly compressed) row height.
export function detailRowFont(rowH) {
  return Math.max(8, Math.min(11, Math.round((rowH || DLAYOUT.PORT_H) * 0.55)));
}

export function detailBoxHeight(portCount, rowH) {
  const rows = Math.max(portCount, 1);
  const rh = rowH || detailRowHeight(portCount);
  return DLAYOUT.HEADER_H + rows * rh + DLAYOUT.PAD_B;
}

export function detailPortCenterY(index, rowH) {
  const rh = rowH || DLAYOUT.PORT_H;
  return DLAYOUT.HEADER_H + index * rh + rh / 2;
}

// Choose which side of each device a port-to-port link should attach to, based
// on the relative box positions. Side-by-side devices connect right<->left;
// vertically stacked devices connect on the SAME side so the cable runs straight
// down that side. Returns [sourceSide, targetSide] with 'l' | 'r'.
export function pickEdgeSides(srcPos, tgtPos, boxW = DLAYOUT.BOX_W) {
  const sL = srcPos.x;
  const sR = srcPos.x + boxW;
  const tL = tgtPos.x;
  const tR = tgtPos.x + boxW;
  const margin = boxW * 0.4;
  if (tL >= sR - margin) return ['r', 'l']; // target clearly to the right
  if (sL >= tR - margin) return ['l', 'r']; // target clearly to the left
  // horizontally overlapping (stacked) -> same side; default to the right
  return ['r', 'r'];
}

// Standard-page "auto" docking: pick the facing sides of two square device
// cards from their relative centre positions. Dominant axis wins — horizontal
// neighbours dock right<->left, vertical neighbours dock bottom<->top — so an
// unset ("auto") connection always leaves each device on the side nearest its
// peer. Returns [sourceSide, targetSide] with 'l' | 'r' | 't' | 'b'.
export function autoStandardSides(srcPos, tgtPos, w = 64, h = 64) {
  if (!srcPos || !tgtPos) return ['r', 'l'];
  const dx = tgtPos.x + w / 2 - (srcPos.x + w / 2);
  const dy = tgtPos.y + h / 2 - (srcPos.y + h / 2);
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? ['r', 'l'] : ['l', 'r'];
  return dy >= 0 ? ['b', 't'] : ['t', 'b'];
}

export function isStandardPage(p) {
  return !p || p.type !== 'detail';
}

export function standardPages(doc) {
  return (doc.pages || []).filter((p) => isStandardPage(p));
}

// True when a node is an ALIAS placement (a clone/view of a master device on
// another standard page). Alias nodes only carry { aliasOf } in their data —
// all real config lives on the master device node.
export function isAliasNode(node) {
  return !!(node && node.data && node.data.aliasOf);
}

// Resolve ANY node id (master OR alias placement) to its canonical master id.
export function resolveMasterId(doc, nodeId) {
  if (!nodeId) return nodeId;
  for (const p of doc.pages || []) {
    const n = (p.nodes || []).find((x) => x.id === nodeId);
    if (n) return n.data?.aliasOf || nodeId;
  }
  return nodeId;
}

// Find a device by id. ALIAS-AWARE: if the id refers to an alias placement, the
// MASTER device node (with the full config) is returned instead. This means
// the Inspector, detail/location rendering, cable lists and design checks all
// transparently operate on the single source of truth.
export function findDeviceGlobal(doc, deviceId) {
  if (!deviceId) return null;
  let found = null;
  for (const p of doc.pages || []) {
    if (!p.nodes) continue;
    const n = p.nodes.find((x) => x.id === deviceId);
    if (n) {
      found = n;
      break;
    }
  }
  if (!found) return null;
  if (found.data?.aliasOf) {
    const masterId = found.data.aliasOf;
    for (const p of doc.pages || []) {
      const m = (p.nodes || []).find((x) => x.id === masterId && !x.data?.aliasOf);
      if (m) return m;
    }
  }
  return found;
}

// Canonical device list across standard pages — MASTER nodes only (alias
// placements are skipped). Use this for aggregations so a device placed on
// several pages is counted exactly once. Returns [{ node, page }].
export function masterDevices(doc) {
  const list = [];
  for (const p of doc.pages || []) {
    for (const n of p.nodes || []) {
      if (n.data?.aliasOf) continue;
      list.push({ node: n, page: p });
    }
  }
  return list;
}

// Build a resolver mapping ANY node id (master + alias placements) to its
// master device node. Lets aggregations resolve an edge endpoint that points
// at an alias id (e.g. a connection drawn on a clone's page).
export function buildNodeResolver(doc) {
  const masters = {};
  const aliasToMaster = {};
  for (const p of doc.pages || []) {
    for (const n of p.nodes || []) {
      if (n.data?.aliasOf) aliasToMaster[n.id] = n.data.aliasOf;
      else masters[n.id] = n;
    }
  }
  return (nodeId) => {
    if (!nodeId) return null;
    if (masters[nodeId]) return masters[nodeId];
    const mid = aliasToMaster[nodeId];
    return mid ? masters[mid] || null : null;
  };
}

// Edges to DISPLAY on a standard page. Connections form a single global
// topology (each edge is stored once and references MASTER device ids). An edge
// is shown on every page where BOTH endpoint devices are present — either as
// the master node or as a clone/alias placement — with its endpoints remapped
// to the local node ids of that page. Edges whose home (storage) page differs
// from the rendered page are flagged `__mirrored` so they can be drawn subtly.
// This never affects aggregations (those use the stored edges once).
export function displayEdgesForPage(doc, page) {
  if (!page || (page.type && page.type !== 'standard')) return [];
  // masterId -> the local node id present on this page (master node or clone).
  const localByMaster = {};
  const posById = {};
  (page.nodes || []).forEach((n) => {
    localByMaster[n.data?.aliasOf || n.id] = n.id;
    posById[n.id] = n.position;
  });
  const out = [];
  const seen = new Set();
  const consider = (edges, isHome) => {
    (edges || []).forEach((e) => {
      if (seen.has(e.id)) return;
      const sMaster = resolveMasterId(doc, e.source);
      const tMaster = resolveMasterId(doc, e.target);
      const ls = localByMaster[sMaster] || localByMaster[e.source];
      const lt = localByMaster[tMaster] || localByMaster[e.target];
      if (!ls || !lt) return; // both endpoints must be present on this page
      seen.add(e.id);
      // Resolve "auto" (unset) docking sides DYNAMICALLY from the local device
      // positions on THIS page, so connections drawn without an explicit side
      // (e.g. created on a detail plan, or set to "Auto") always dock on the
      // side facing their peer instead of a fixed default. Explicit sides win.
      let sSide = e.sourceSide;
      let tSide = e.targetSide;
      if (!sSide || !tSide) {
        const [as, at] = autoStandardSides(posById[ls], posById[lt]);
        if (!sSide) sSide = as;
        if (!tSide) tSide = at;
      }
      out.push({ ...e, source: ls, target: lt, sourceSide: sSide, targetSide: tSide, __mirrored: !isHome });
    });
  };
  // The page's OWN edges are always the home (non-mirrored) set.
  consider(page.edges, true);
  // Other standard pages contribute MIRRORED edges where both ends are present.
  (doc.pages || []).forEach((p) => {
    if (p.id === page.id) return;
    if (p.type && p.type !== 'standard') return;
    consider(p.edges, false);
  });
  return out;
}

export function deviceDisplayName(doc, deviceId, lang = 'de') {
  const n = findDeviceGlobal(doc, deviceId);
  if (!n) return '?';
  return n.data?.label || deviceLabel(n.data?.deviceType, lang);
}

export function portIndexGlobal(doc, deviceId, portId) {
  const n = findDeviceGlobal(doc, deviceId);
  if (!n || !portId) return -1;
  return (n.data?.ports || []).findIndex((p) => p.id === portId);
}

export function portNameGlobal(doc, deviceId, portId) {
  const n = findDeviceGlobal(doc, deviceId);
  if (!n || !portId) return '';
  const p = (n.data?.ports || []).find((x) => x.id === portId);
  return p ? p.name : '';
}

// All edges across all standard pages.
export function getAllEdges(doc) {
  const out = [];
  for (const p of doc.pages || []) {
    if (!p.edges) continue;
    for (const e of p.edges) out.push(e);
  }
  return out;
}

// Find an edge by id across all pages and return { edge, page } (or null).
// Used by the Inspector so a connection selected on a DETAIL page can be edited
// even though the edge itself is stored on its owning standard page.
export function findEdgeGlobal(doc, edgeId) {
  if (!edgeId) return null;
  for (const p of doc.pages || []) {
    const e = (p.edges || []).find((x) => x.id === edgeId);
    if (e) return { edge: e, page: p };
  }
  return null;
}

// All LAGs across every page, keyed by id — lets detail/location views resolve
// a member edge's LAG (name/color) even though the LAG lives on a standard page.
export function getLagMap(doc) {
  const map = {};
  for (const p of doc.pages || []) {
    for (const l of p.lags || []) map[l.id] = l;
  }
  return map;
}

// List of devices (across standard pages) that can still be added to this detail page.
export function availableDevicesForDetail(doc, page, lang = 'de') {
  const added = new Set((page.members || []).map((m) => m.deviceId));
  const list = [];
  for (const p of standardPages(doc)) {
    for (const n of p.nodes || []) {
      if (n.data?.aliasOf) continue; // skip alias placements — only master devices
      if (added.has(n.id)) continue;
      list.push({ id: n.id, name: n.data?.label || deviceLabel(n.data?.deviceType, lang), deviceType: n.data?.deviceType, page: p.name });
    }
  }
  return list;
}

// Derive the renderable model for a detail page.
// Returns { devices:[{deviceId, node, position, ports}], internalEdges:[...], stubs:[...] }
// internalEdges require BOTH member ports assigned. stubs require the member port assigned.
export function computeDetailModel(doc, page) {
  const members = page.members || [];
  const memberSet = new Set(members.map((m) => m.deviceId));
  const devices = members
    .map((m) => {
      const node = findDeviceGlobal(doc, m.deviceId);
      if (!node) return null;
      return { deviceId: m.deviceId, node, position: m.position || { x: 0, y: 0 }, ports: node.data?.ports || [] };
    })
    .filter(Boolean);

  const internalEdges = [];
  const stubs = [];
  getAllEdges(doc).forEach((e) => {
    const sIn = memberSet.has(e.source);
    const tIn = memberSet.has(e.target);
    if (sIn && tIn) {
      if (e.sourcePortId && e.targetPortId) {
        internalEdges.push({
          id: e.id,
          sourceDeviceId: e.source,
          sourcePortId: e.sourcePortId,
          targetDeviceId: e.target,
          targetPortId: e.targetPortId,
          connType: e.connType || 'ethernet',
          lagId: e.lagId || null,
        });
      }
    } else if (sIn && !tIn) {
      if (e.sourcePortId) {
        stubs.push({ id: e.id, deviceId: e.source, portId: e.sourcePortId, otherDeviceId: e.target, otherPortId: e.targetPortId, connType: e.connType || 'ethernet' });
      }
    } else if (!sIn && tIn) {
      if (e.targetPortId) {
        stubs.push({ id: e.id, deviceId: e.target, portId: e.targetPortId, otherDeviceId: e.source, otherPortId: e.sourcePortId, connType: e.connType || 'ethernet' });
      }
    }
  });

  return { devices, internalEdges, stubs };
}
